import { ReactNode } from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Props accepted by the `role="tablist"` element.
 *
 * `React.HTMLAttributes` alone rejects `data-*` keys in an object literal — JSX
 * permits them on an element, but nothing types them on a plain object. The
 * index signature is what lets a consumer pass `data-component`, which is one
 * of the two things this element could not previously be given.
 */
export type TablistProps = React.HTMLAttributes<HTMLDivElement> & {
  [dataAttribute: `data-${string}`]: unknown;
};

export interface TabGroupContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The tab elements to render inside the container */
  children: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Enable horizontal scrolling for long tab rows */
  scrollable?: boolean;
  /** Whether to show edge fade shadows when scrollable */
  withScrollShadow?: boolean;
  /**
   * Props for the element that carries `role="tablist"`.
   *
   * Kept separate from the spread props because the two land in different
   * places when `scrollable` is set: the spread goes to the outer wrapper
   * (which owns positioning and the scroll shadows), while the tablist is an
   * inner div. An accessible name has to reach the tablist specifically —
   * `aria-label` on the wrapper names the wrong element.
   */
  tablistProps?: TablistProps;
}

/**
 * Container component that provides layout for tabs.
 * The consuming app is responsible for rendering the actual tab elements.
 *
 * @example
 * ```tsx
 * <TabGroupContent scrollable className="max-w-md">
 *   {tabs.map(({ tab, state, onToggle }) => (
 *     <Tab
 *       key={tab.id}
 *       data-state={state}
 *       onClick={onToggle}
 *     >
 *       {tab.data.label}
 *     </Tab>
 *   ))}
 * </TabGroupContent>
 * ```
 */
export function TabGroupContent({
  children,
  className,
  scrollable = false,
  withScrollShadow = true,
  tablistProps,
  ...props
}: TabGroupContentProps) {
  if (!scrollable) {
    // Root and tablist are the same element here, so both prop sets land on it.
    return (
      <div
        role="tablist"
        className={cn('inline-flex flex-wrap items-center gap-2', className)}
        {...props}
        {...tablistProps}
      >
        {children}
      </div>
    );
  }

  return (
    /* No `overflow-hidden` here, deliberately.

       A tab's focus ring is a triple box-shadow reaching 5px past its border
       box (`.tab:focus-visible` in tab.css). A box shadow is not part of a
       scroll container's overflow area, so any ancestor that clips trims it.
       This wrapper used to clip, and the inner tablist still does — per spec
       `overflow-x: auto` forces the block axis to clip too, so there is no way
       to scroll horizontally and let a ring escape vertically.

       The inner tablist therefore reserves the ring's extent as padding and
       pulls it straight back with an equal negative margin: tab positions are
       unchanged, the ring has room to paint, and the scroll extents gain the
       same room so a ring is not trimmed at either end of the row. That
       overhang then needs somewhere to land, which is why this wrapper no
       longer clips — padding it instead would collide with a consumer's own
       padding through `className`.

       Containment is unaffected: the inner tablist's `overflow-x-auto` is what
       actually clips overflowing tabs, and `min-w-0` is what lets this shrink
       in a flex parent. Neither depended on `overflow-hidden`. */
    <div className={cn('relative min-w-0', className)} {...props}>
      <div
        role="tablist"
        className="flex items-center gap-2 overflow-x-auto whitespace-nowrap
                   p-[5px] -m-[5px]
                   [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        {...tablistProps}
      >
        {children}
      </div>
      {withScrollShadow && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 h-full w-6
                       bg-gradient-to-r from-background to-transparent"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-full w-6
                       bg-gradient-to-l from-background to-transparent"
          />
        </>
      )}
    </div>
  );
}
